import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';

interface Profile {
  id: number;
  name: string;
  title: string;
  image: string;
  description: string;
}

const SceneContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  color: #fff;
  padding: 2rem;
`;

const SceneTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 0 0 3rem 0;
  text-align: center;
  background: linear-gradient(45deg, #fff, #00ff88);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba(0, 255, 136, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 2rem;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProfileCard = styled.div<{ isActive: boolean }>`
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid ${props => props.isActive ? '#00ff88' : 'rgba(0, 255, 136, 0.3)'};
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(0, 255, 136, 0.1) 0%, 
      transparent 50%, 
      rgba(0, 255, 136, 0.05) 100%);
    opacity: ${props => props.isActive ? 1 : 0};
    transition: opacity 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: #00ff88;
    box-shadow: 0 20px 40px rgba(0, 255, 136, 0.2);
    
    &::before {
      opacity: 1;
    }
  }
  
  .profile-image {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    margin: 0 auto 1.5rem;
    background: linear-gradient(45deg, #00ff88, #00cc6a);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    color: #000;
    font-weight: bold;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      top: -5px;
      left: -5px;
      right: -5px;
      bottom: -5px;
      border-radius: 50%;
      background: linear-gradient(45deg, #00ff88, #00cc6a);
      opacity: 0.3;
      filter: blur(10px);
      animation: pulse 2s ease-in-out infinite;
    }
    
    @keyframes pulse {
      0%, 100% { opacity: 0.3; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.1); }
    }
  }
  
  .profile-name {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .profile-title {
    font-size: 1rem;
    color: #00ff88;
    margin: 0 0 1.5rem 0;
    font-weight: 300;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.8;
  }
  
  .profile-description {
    font-size: 0.9rem;
    color: #ccc;
    line-height: 1.6;
    margin: 0;
    opacity: 0.7;
  }
  
  .watch-button {
    margin-top: 1.5rem;
    padding: 0.8rem 2rem;
    background: ${props => props.isActive ? '#00ff88' : 'transparent'};
    color: ${props => props.isActive ? '#000' : '#00ff88'};
    border: 2px solid #00ff88;
    border-radius: 25px;
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s ease;
    }
    
    &:hover::before {
      left: 100%;
    }
    
    &:hover {
      background: #00ff88;
      color: #000;
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(0, 255, 136, 0.3);
    }
  }
  
  .hex-icon {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 30px;
    height: 30px;
    background: ${props => props.isActive ? '#00ff88' : 'rgba(0, 255, 136, 0.3)'};
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    color: ${props => props.isActive ? '#000' : '#00ff88'};
    transition: all 0.3s ease;
  }
`;

const GlowingBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
  
  .glow-circle {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0, 255, 136, 0.1), transparent);
    animation: float 6s ease-in-out infinite;
    
    &:nth-child(1) {
      width: 200px;
      height: 200px;
      top: 10%;
      left: 10%;
      animation-delay: 0s;
    }
    
    &:nth-child(2) {
      width: 150px;
      height: 150px;
      top: 60%;
      right: 15%;
      animation-delay: 2s;
    }
    
    &:nth-child(3) {
      width: 100px;
      height: 100px;
      bottom: 20%;
      left: 20%;
      animation-delay: 4s;
    }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px) scale(1); opacity: 0.3; }
    50% { transform: translateY(-20px) scale(1.1); opacity: 0.6; }
  }
`;

const ProfileScene: React.FC = () => {
  const sceneRef = useRef<HTMLDivElement>(null);
  const [activeProfile, setActiveProfile] = useState<number | null>(null);

  const profiles: Profile[] = [
    {
      id: 1,
      name: "Alicia Díaz",
      title: "Sculptor",
      image: "👩‍🎨",
      description: "Creating masterpieces that bridge the gap between imagination and reality."
    },
    {
      id: 2,
      name: "Naya",
      title: "Software Engineer",
      image: "👩‍💻",
      description: "Building digital solutions that empower and inspire the next generation."
    },
    {
      id: 3,
      name: "Reyna Noriega",
      title: "Visual Artist & Author",
      image: "👩‍🎭",
      description: "Telling stories through art that challenge perceptions and ignite creativity."
    },
    {
      id: 4,
      name: "Joan Marie",
      title: "Space Engineer",
      image: "👩‍🚀",
      description: "Pushing the boundaries of human exploration and technological innovation."
    }
  ];

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Animate profile cards on scene enter
    gsap.fromTo('.profile-card', 
      { 
        opacity: 0, 
        y: 100,
        scale: 0.8
      },
      { 
        opacity: 1, 
        y: 0,
        scale: 1,
        duration: 1,
        ease: "power2.out",
        stagger: 0.2
      }
    );

  }, []);

  const handleProfileClick = (profileId: number) => {
    setActiveProfile(activeProfile === profileId ? null : profileId);
  };

  return (
    <SceneContainer ref={sceneRef} className="scene">
      <GlowingBackground>
        <div className="glow-circle"></div>
        <div className="glow-circle"></div>
        <div className="glow-circle"></div>
      </GlowingBackground>
      
      <SceneTitle>Meet the Visionaries</SceneTitle>
      
      <ProfileGrid>
        {profiles.map((profile) => (
          <ProfileCard
            key={profile.id}
            className="profile-card"
            isActive={activeProfile === profile.id}
            onClick={() => handleProfileClick(profile.id)}
          >
            <div className="hex-icon">
              {activeProfile === profile.id ? '✓' : '👁️'}
            </div>
            
            <div className="profile-image">
              {profile.image}
            </div>
            
            <h3 className="profile-name">{profile.name}</h3>
            <p className="profile-title">{profile.title}</p>
            <p className="profile-description">{profile.description}</p>
            
            <button className="watch-button">
              {activeProfile === profile.id ? 'Watched' : 'Watch'}
            </button>
          </ProfileCard>
        ))}
      </ProfileGrid>
    </SceneContainer>
  );
};

export default ProfileScene;
